import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { Plus, Pencil, Trash2, Save, X, GraduationCap, RefreshCw } from 'lucide-react';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { Textarea } from '../ui/textarea';
import { Badge } from '../ui/badge';
import { toast } from 'sonner';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const EMPTY = {
  hotmart_product_id: '', program_code: '', name: '', category: 'Formação Profissional',
  knowledge_area: '', modality: 'EaD Gravado', class_code: '', workload: '', professor: '',
  academic_team: '', certification: '', prerequisites: '', schedule_ref: '', platform: '',
  access_period: '', language: 'Português', objective: '', public_price: 0,
  benefits: [], payment_methods: [], doc_anexo_i: 'AJ-ANX-001',
  doc_plano_financeiro: 'AJ-FIN-001', doc_termo: 'AJ-TER-001',
};

const MODALITIES = ['EaD Gravado', 'Elite – Transmissão ao Vivo', 'Elite – Presencial', 'Mentor', 'Híbrido', 'Outra'];

const AdminPrograms = () => {
  const [programs, setPrograms] = useState([]);
  const [editing, setEditing] = useState(null); // program object or null
  const [loading, setLoading] = useState(true);
  const [apiConfigured, setApiConfigured] = useState(null);
  const [importValue, setImportValue] = useState('');
  const [importKind, setImportKind] = useState('transaction');
  const [importing, setImporting] = useState(false);
  const [syncing, setSyncing] = useState(false);
  const [syncResult, setSyncResult] = useState(null);
  const [hotmartProducts, setHotmartProducts] = useState([]);
  const [showCatalog, setShowCatalog] = useState(false);
  const [lastSync, setLastSync] = useState(null);
  const [testingConn, setTestingConn] = useState(false);

  const load = async () => {
    try {
      const res = await axios.get(`${API}/admin/programs`, { withCredentials: true });
      setPrograms(res.data);
    } catch (e) {
      toast.error('Erro ao carregar programas');
    } finally {
      setLoading(false);
    }
  };

  const loadApiStatus = async () => {
    try {
      const res = await axios.get(`${API}/admin/hotmart/status`, { withCredentials: true });
      setApiConfigured(res.data.configured);
    } catch (e) { setApiConfigured(false); }
  };

  const loadSyncStatus = async () => {
    try {
      const res = await axios.get(`${API}/admin/hotmart/sync-status`, { withCredentials: true });
      setLastSync(res.data.last_sync);
    } catch (e) { /* ignore */ }
  };

  const testConnection = async () => {
    setTestingConn(true);
    try {
      const res = await axios.get(`${API}/admin/hotmart/test-connection`, { withCredentials: true });
      if (res.data.connected) toast.success(res.data.message || 'Conexão OK');
      else toast.error(res.data.detail || 'Falha na conexão');
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Falha ao testar conexão');
    } finally { setTestingConn(false); }
  };

  useEffect(() => { load(); loadApiStatus(); loadSyncStatus(); }, []);

  const importSale = async () => {
    if (!importValue.trim()) return;
    setImporting(true);
    try {
      const payload = importKind === 'transaction' ? { transaction: importValue.trim() } : { buyer_email: importValue.trim() };
      const res = await axios.post(`${API}/admin/contracts/import`, payload, { withCredentials: true });
      toast.success(`Importado(s): ${res.data.imported} contrato(s)`);
      setImportValue('');
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Falha na importação');
    } finally { setImporting(false); }
  };

  const syncProducts = async () => {
    setSyncing(true);
    setSyncResult(null);
    try {
      const res = await axios.post(`${API}/admin/hotmart/sync-products`, {}, { withCredentials: true });
      setSyncResult(res.data);
      toast.success('Sincronização concluída');
      loadSyncStatus();
      if (showCatalog) loadCatalog();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Falha na sincronização');
    } finally { setSyncing(false); }
  };

  const loadCatalog = async () => {
    try {
      const res = await axios.get(`${API}/admin/hotmart/products`, { withCredentials: true });
      setHotmartProducts(res.data);
      setShowCatalog(true);
    } catch (e) { toast.error('Erro ao carregar catálogo'); }
  };


  const save = async () => {
    const payload = {
      ...editing,
      public_price: parseFloat(editing.public_price) || 0,
      benefits: typeof editing.benefits === 'string' ? editing.benefits.split(',').map(s => s.trim()).filter(Boolean) : editing.benefits,
      payment_methods: typeof editing.payment_methods === 'string' ? editing.payment_methods.split(',').map(s => s.trim()).filter(Boolean) : editing.payment_methods,
    };
    try {
      if (editing.id) {
        await axios.put(`${API}/admin/programs/${editing.id}`, payload, { withCredentials: true });
        toast.success('Programa atualizado');
      } else {
        await axios.post(`${API}/admin/programs`, payload, { withCredentials: true });
        toast.success('Programa criado');
      }
      setEditing(null);
      load();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Erro ao salvar');
    }
  };

  const remove = async (id) => {
    if (!window.confirm('Excluir este programa?')) return;
    try {
      await axios.delete(`${API}/admin/programs/${id}`, { withCredentials: true });
      toast.success('Programa excluído');
      load();
    } catch (e) {
      toast.error('Erro ao excluir');
    }
  };

  const startEdit = (p) => setEditing({
    ...p,
    benefits: (p.benefits || []).join(', '),
    payment_methods: (p.payment_methods || []).join(', '),
  });

  const field = (label, key, opts = {}) => (
    <div>
      <Label className="text-gray-300 text-xs">{label}</Label>
      {opts.type === 'textarea' ? (
        <Textarea className="bg-zinc-800 border-zinc-700 text-white" value={editing[key] || ''}
          onChange={e => setEditing({ ...editing, [key]: e.target.value })} data-testid={`program-field-${key}`} />
      ) : opts.options ? (
        <select className="w-full bg-zinc-800 border border-zinc-700 text-white rounded-md h-10 px-3" value={editing[key] || ''}
          onChange={e => setEditing({ ...editing, [key]: e.target.value })} data-testid={`program-field-${key}`}>
          {opts.options.map(o => <option key={o} value={o}>{o}</option>)}
        </select>
      ) : (
        <Input className="bg-zinc-800 border-zinc-700 text-white" type={opts.type || 'text'} value={editing[key] ?? ''}
          onChange={e => setEditing({ ...editing, [key]: e.target.value })} data-testid={`program-field-${key}`} />
      )}
    </div>
  );

  if (editing) {
    return (
      <div className="bg-zinc-900 border border-zinc-800 rounded-xl p-6" data-testid="program-editor">
        <div className="flex items-center justify-between mb-6">
          <h3 className="text-xl font-bold text-white">{editing.id ? 'Editar' : 'Novo'} Programa Educacional</h3>
          <Button variant="ghost" onClick={() => setEditing(null)}><X size={18} /></Button>
        </div>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          {field('ID Produto Hotmart *', 'hotmart_product_id')}
          {field('Código do Programa *', 'program_code')}
          {field('Nome do Programa *', 'name')}
          {field('Categoria', 'category')}
          {field('Área de Conhecimento', 'knowledge_area')}
          {field('Modalidade *', 'modality', { options: MODALITIES })}
          {field('Código da Turma', 'class_code')}
          {field('Carga Horária *', 'workload')}
          {field('Professor Responsável *', 'professor')}
          {field('Equipe Acadêmica', 'academic_team')}
          {field('Certificação *', 'certification')}
          {field('Pré-requisitos', 'prerequisites')}
          {field('Cronograma (schedule_id/ref)', 'schedule_ref')}
          {field('Plataforma / AVA', 'platform')}
          {field('Prazo de Acesso', 'access_period')}
          {field('Idioma', 'language')}
          {field('Valor Público (R$) *', 'public_price', { type: 'number' })}
          {field('Formas de Pagamento (vírgula)', 'payment_methods')}
        </div>
        <div className="grid grid-cols-1 gap-4 mt-4">
          {field('Objetivo do Programa', 'objective', { type: 'textarea' })}
          {field('Benefícios Incluídos (separados por vírgula)', 'benefits', { type: 'textarea' })}
        </div>
        <div className="grid grid-cols-3 gap-4 mt-4">
          {field('Modelo Anexo I', 'doc_anexo_i')}
          {field('Modelo Plano Financeiro', 'doc_plano_financeiro')}
          {field('Modelo Termo', 'doc_termo')}
        </div>
        <div className="flex gap-3 mt-6">
          <Button onClick={save} className="bg-red-600 hover:bg-red-700" data-testid="program-save-btn"><Save size={16} className="mr-2" />Salvar</Button>
          <Button variant="outline" onClick={() => setEditing(null)}>Cancelar</Button>
        </div>
      </div>
    );
  }

  return (
    <div data-testid="admin-programs">
      {/* Importar venda via API oficial Hotmart */}
      <div className="bg-zinc-900 border border-zinc-800 rounded-xl p-5 mb-6" data-testid="hotmart-import-panel">
        <div className="flex items-center justify-between mb-3">
          <h3 className="text-white font-semibold flex items-center gap-2">Integração Hotmart (API oficial)</h3>
          <Badge className={apiConfigured ? 'bg-green-600' : 'bg-amber-600'}>
            {apiConfigured === null ? 'Verificando...' : apiConfigured ? 'API configurada' : 'API não configurada'}
          </Badge>
        </div>

        {/* Sincronizar Produtos */}
        <div className="flex flex-wrap items-center gap-3 mb-2">
          <Button onClick={syncProducts} disabled={!apiConfigured || syncing} className="bg-red-600 hover:bg-red-700" data-testid="sync-products-btn">
            <RefreshCw size={16} className={`mr-2 ${syncing ? 'animate-spin' : ''}`} />
            {syncing ? 'Sincronizando...' : 'Sincronizar Produtos Hotmart'}
          </Button>
          <Button variant="outline" onClick={testConnection} disabled={!apiConfigured || testingConn} data-testid="test-connection-btn">
            {testingConn ? 'Testando...' : 'Testar conexão'}
          </Button>
          <Button variant="outline" onClick={loadCatalog} disabled={!apiConfigured} data-testid="view-catalog-btn">Ver catálogo sincronizado</Button>
        </div>
        {lastSync && (
          <p className="text-xs text-gray-400 mb-4" data-testid="last-sync-info">
            Última sincronização: {new Date(lastSync.synced_at).toLocaleString('pt-BR')} · {lastSync.found} produtos · {lastSync.errors_count} erro(s) · <span className="uppercase">{lastSync.trigger === 'automatic' ? 'automática' : 'manual'}</span> · Próxima automática a cada 24h
          </p>
        )}

        {syncResult && (
          <div className="bg-black/40 border border-zinc-700 rounded-lg p-4 mb-4" data-testid="sync-result">
            <p className="text-green-400 font-semibold mb-2">✅ {syncResult.message}</p>
            <div className="grid grid-cols-2 sm:grid-cols-5 gap-3 text-center">
              <div><div className="text-2xl font-bold text-white">{syncResult.found}</div><div className="text-xs text-gray-400">Encontrados</div></div>
              <div><div className="text-2xl font-bold text-green-400">{syncResult.new}</div><div className="text-xs text-gray-400">Novos</div></div>
              <div><div className="text-2xl font-bold text-blue-400">{syncResult.updated}</div><div className="text-xs text-gray-400">Atualizados</div></div>
              <div><div className="text-2xl font-bold text-amber-400">{syncResult.inactive}</div><div className="text-xs text-gray-400">Inativos</div></div>
              <div><div className={`text-2xl font-bold ${syncResult.errors_count ? 'text-red-400' : 'text-gray-300'}`}>{syncResult.errors_count}</div><div className="text-xs text-gray-400">Erros</div></div>
            </div>
            {syncResult.errors_count > 0 && (
              <ul className="mt-3 text-xs text-red-300 list-disc list-inside max-h-32 overflow-y-auto">
                {syncResult.errors.map((er, i) => <li key={i}>{er.product_id || er.product}: {er.error}</li>)}
              </ul>
            )}
          </div>
        )}

        {/* Importar venda por transação/e-mail */}
        {apiConfigured ? (
          <div className="flex flex-col sm:flex-row gap-2">
            <select className="bg-zinc-800 border border-zinc-700 text-white rounded-md h-10 px-3" value={importKind} onChange={e => setImportKind(e.target.value)} data-testid="import-kind">
              <option value="transaction">Transação</option>
              <option value="buyer_email">E-mail do comprador</option>
            </select>
            <Input className="bg-zinc-800 border-zinc-700 text-white flex-1" placeholder={importKind === 'transaction' ? 'HP...' : 'comprador@email.com'} value={importValue} onChange={e => setImportValue(e.target.value)} data-testid="import-value" />
            <Button onClick={importSale} disabled={importing} variant="outline" data-testid="import-submit">{importing ? 'Importando...' : 'Importar venda'}</Button>
          </div>
        ) : (
          <p className="text-gray-400 text-sm">Configure as credenciais da API (Client ID, Client Secret e Basic Token) para sincronizar produtos e consultar vendas. Enquanto isso, o webhook continua criando os contratos automaticamente.</p>
        )}

        {showCatalog && (
          <div className="mt-4 overflow-x-auto max-h-96 overflow-y-auto" data-testid="hotmart-catalog">
            <table className="w-full text-xs">
              <thead className="sticky top-0 bg-zinc-900">
                <tr className="text-left text-gray-400 border-b border-zinc-800">
                  <th className="py-2 px-2">ID</th><th className="py-2 px-2">Nome</th><th className="py-2 px-2">Status</th><th className="py-2 px-2">Tipo</th><th className="py-2 px-2">ucode</th>
                </tr>
              </thead>
              <tbody>
                {hotmartProducts.map(p => (
                  <tr key={p.id} className="border-b border-zinc-800/60 text-gray-200">
                    <td className="py-1.5 px-2 font-mono">{p.hotmart_product_id}</td>
                    <td className="py-1.5 px-2">{p.nome}</td>
                    <td className="py-1.5 px-2"><span className={p.status === 'ACTIVE' ? 'text-green-400' : 'text-amber-400'}>{p.status}</span></td>
                    <td className="py-1.5 px-2">{p.tipo}</td>
                    <td className="py-1.5 px-2 font-mono text-[10px]">{p.ucode}</td>
                  </tr>
                ))}
                {hotmartProducts.length === 0 && <tr><td colSpan="5" className="py-4 text-center text-gray-500">Nenhum produto sincronizado ainda</td></tr>}
              </tbody>
            </table>
          </div>
        )}
      </div>

      <div className="flex items-center justify-between mb-4">
        <div>
          <h3 className="text-xl font-bold text-white flex items-center gap-2"><GraduationCap className="text-red-500" size={20} />Cadastro de Programas Educacionais</h3>
          <p className="text-gray-400 text-sm">Mapeamento ID Hotmart → Programa (usado pelo Motor de Contratação)</p>
        </div>
        <Button onClick={() => setEditing({ ...EMPTY })} className="bg-red-600 hover:bg-red-700" data-testid="program-new-btn"><Plus size={16} className="mr-2" />Novo Programa</Button>
      </div>
      {loading ? <p className="text-gray-400">Carregando...</p> : (
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead>
              <tr className="text-left text-gray-400 border-b border-zinc-800">
                <th className="py-2 px-2">Código</th><th className="py-2 px-2">Nome</th>
                <th className="py-2 px-2">ID Hotmart</th><th className="py-2 px-2">Modalidade</th>
                <th className="py-2 px-2">Valor</th><th className="py-2 px-2">Ações</th>
              </tr>
            </thead>
            <tbody>
              {programs.map(p => (
                <tr key={p.id} className="border-b border-zinc-800/60 text-gray-200" data-testid={`program-row-${p.program_code}`}>
                  <td className="py-2 px-2 font-mono text-red-400">{p.program_code}</td>
                  <td className="py-2 px-2">{p.name}</td>
                  <td className="py-2 px-2 font-mono text-xs">{p.hotmart_product_id}</td>
                  <td className="py-2 px-2">{p.modality}</td>
                  <td className="py-2 px-2">R$ {Number(p.public_price).toLocaleString('pt-BR')}</td>
                  <td className="py-2 px-2 flex gap-2">
                    <button onClick={() => startEdit(p)} className="text-blue-400 hover:text-blue-300" data-testid={`program-edit-${p.program_code}`}><Pencil size={16} /></button>
                    <button onClick={() => remove(p.id)} className="text-red-400 hover:text-red-300"><Trash2 size={16} /></button>
                  </td>
                </tr>
              ))}
              {programs.length === 0 && <tr><td colSpan="6" className="py-6 text-center text-gray-500">Nenhum programa cadastrado</td></tr>}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
};

export default AdminPrograms;
